import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Session } from '@supabase/supabase-js';
import { SUPABASE_URL } from '@/lib/supabase';
import { logger } from '@/lib/logger';

/**
 * supabase-js refuses to hand back an EXPIRED session when the refresh call
 * fails (even for a plain network error), which would bounce an offline user
 * to the login screen. This reads the session it persisted itself so the app
 * can keep the user signed in while offline; supabase-js refreshes it
 * normally once connectivity returns (the refresh token is untouched).
 */
export function persistedSessionStorageKey(): string | null {
  try {
    const ref = new URL(SUPABASE_URL).hostname.split('.')[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch {
    return null;
  }
}

export async function readPersistedSession(): Promise<Session | null> {
  const key = persistedSessionStorageKey();
  if (!key) return null;
  try {
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user?.id) return null;
    return parsed as Session;
  } catch (err) {
    logger.warn('[offline] could not read persisted session', err);
    return null;
  }
}
